import React, { useState, useEffect } from 'react';
import { pagosAPI, obrasAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, CreditCard, CheckCircle } from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor, translateEstado, getTodayISO, addDaysToDate } from '../lib/utils';

const PagosPage = () => {
  const [pagos, setPagos] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPago, setSelectedPago] = useState(null);
  const [formData, setFormData] = useState({
    proveedor: '',
    concepto: '',
    importe: 0,
    fecha_vencimiento: addDaysToDate(getTodayISO(), 30),
    fecha_pago: '',
    estado: 'pendiente',
    obra_id: '',
    notas: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pagosRes, obrasRes] = await Promise.all([
        pagosAPI.list(),
        obrasAPI.list(),
      ]);
      setPagos(pagosRes.data);
      setObras(obrasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPago) {
        await pagosAPI.update(selectedPago.id, formData);
        toast.success('Pago actualizado');
      } else {
        await pagosAPI.create(formData);
        toast.success('Pago registrado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar pago');
    }
  };

  const handleEdit = (pago) => {
    setSelectedPago(pago);
    setFormData({
      proveedor: pago.proveedor || '',
      concepto: pago.concepto || '',
      importe: pago.importe || 0,
      fecha_vencimiento: pago.fecha_vencimiento || addDaysToDate(getTodayISO(), 30),
      fecha_pago: pago.fecha_pago || '',
      estado: pago.estado || 'pendiente',
      obra_id: pago.obra_id || '',
      notas: pago.notas || '',
    });
    setDialogOpen(true);
  };

  const handleMarcarPagado = async (pago) => {
    try {
      await pagosAPI.updateEstado(pago.id, 'pagado', getTodayISO());
      toast.success('Pago marcado como pagado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar pago');
    }
  };

  const handleDelete = async () => {
    try {
      await pagosAPI.delete(selectedPago.id);
      toast.success('Pago eliminado');
      setDeleteDialogOpen(false);
      setSelectedPago(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar pago');
    }
  };

  const resetForm = () => {
    setSelectedPago(null);
    setFormData({
      proveedor: '',
      concepto: '',
      importe: 0,
      fecha_vencimiento: addDaysToDate(getTodayISO(), 30),
      fecha_pago: '',
      estado: 'pendiente',
      obra_id: '',
      notas: '',
    });
  };

  const getObraName = (obraId) => {
    const obra = obras.find((o) => o.id === obraId);
    return obra ? `${obra.numero} - ${obra.titulo}` : '-';
  };

  const filteredPagos = pagos.filter((p) => {
    const matchSearch = p.proveedor?.toLowerCase().includes(search.toLowerCase()) ||
      p.concepto?.toLowerCase().includes(search.toLowerCase());
    const matchEstado = !filterEstado || p.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  const totalPendiente = filteredPagos
    .filter(p => p.estado === 'pendiente')
    .reduce((sum, p) => sum + (p.importe || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pagos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pagos a Proveedores</h1>
          <p className="text-slate-500 text-sm mt-1">
            Pendiente: {formatCurrency(totalPendiente)}
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="new-pago-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Pago
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por proveedor o concepto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-pagos-input"
          />
        </div>
        <Select value={filterEstado || "all"} onValueChange={(v) => setFilterEstado(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="filter-estado-select">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="pagado">Pagado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filteredPagos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <CreditCard className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{search || filterEstado ? 'No se encontraron pagos' : 'No hay pagos'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Proveedor</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Concepto</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Obra</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Estado</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Importe</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Vencimiento</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPagos.map((pago) => {
                    const isOverdue = pago.estado === 'pendiente' && 
                      new Date(pago.fecha_vencimiento) < new Date();
                    return (
                      <tr key={pago.id} className="hover:bg-slate-50" data-testid={`pago-row-${pago.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{pago.proveedor}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{pago.concepto}</td>
                        <td className="px-4 py-3 text-sm text-slate-500 max-w-xs truncate">
                          {getObraName(pago.obra_id)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${isOverdue ? 'bg-red-100 text-red-700' : getStatusColor(pago.estado)}`}>
                            {isOverdue ? 'Vencido' : translateEstado(pago.estado)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-medium">
                          {formatCurrency(pago.importe)}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                          {formatDate(pago.fecha_vencimiento)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            {pago.estado === 'pendiente' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleMarcarPagado(pago)}
                                title="Marcar como pagado"
                                data-testid={`mark-paid-${pago.id}`}
                              >
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(pago)}
                              data-testid={`edit-pago-${pago.id}`}
                            >
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => { setSelectedPago(pago); setDeleteDialogOpen(true); }}
                              data-testid={`delete-pago-${pago.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-slate-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pago-dialog">
          <DialogHeader>
            <DialogTitle>{selectedPago ? 'Editar Pago' : 'Nuevo Pago'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Proveedor *</Label>
              <Input
                value={formData.proveedor}
                onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                placeholder="Nombre del proveedor"
                required
                data-testid="pago-proveedor-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Input
                value={formData.concepto}
                onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                placeholder="Descripción del pago"
                required
                data-testid="pago-concepto-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Obra (opcional)</Label>
              <Select
                value={formData.obra_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, obra_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="pago-obra-select">
                  <SelectValue placeholder="Vincular a obra" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin obra</SelectItem>
                  {obras.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importe (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.importe}
                  onChange={(e) => setFormData({ ...formData, importe: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="pago-importe-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha Vencimiento *</Label>
                <Input
                  type="date"
                  value={formData.fecha_vencimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                  required
                  data-testid="pago-vencimiento-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
                data-testid="pago-notas-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-pago-btn">
                {selectedPago ? 'Guardar' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pago?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el pago a "{selectedPago?.proveedor}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-pago-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PagosPage;
